import { useCallback } from 'react';
import { TCHOP_ROOT_URL } from '../../env';
import { prepareHeaders } from '../../services';
import { getOrganisationDomain } from '../utils';

export enum FileAcceptType {
  IMAGE = 'image/*',
  AUDIO = '.mp3,.wav,.aiff',
  VIDEO = 'video/*',
  PDF = '.pdf',
  FILE = '.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,.odt,.ods,.odp',
}

export enum UploadType {
  IMAGE = 'image',
  AUDIO = 'audio',
  VIDEO = 'video',
  PDF = 'pdf',
  FILE = 'file',
}

export enum ImageUploadType {
  PROFILE = 'profile',
  ITEM = 'item',
  COMMENT = 'comment',
  STORY = 'story',
}

export interface UploadHandlerArgs {
  file: File;
  type: UploadType;
  imageUploadType?: ImageUploadType;
}

interface UploadCompletedArgs {
  assemblyId: string;
  callback?: () => void;
}

export const useUpload = () => {
  const onUploadCompleted = useCallback(({ assemblyId, callback }: UploadCompletedArgs) => {
    const interval = setInterval(async () => {
      const { status } = await fetch(
        `${TCHOP_ROOT_URL}/api/fs/assembly/${assemblyId}/status?organisation=${getOrganisationDomain()}`,
        { credentials: 'include', headers: prepareHeaders(new Headers()) }
      ).then((result) => result.json());

      if (status === 'ok') {
        callback?.();
        clearInterval(interval);
      }
    }, 1500);
  }, []);

  const getUploadType = useCallback(({ acceptType }: { acceptType: FileAcceptType }) => {
    switch (acceptType) {
      case FileAcceptType.AUDIO:
        return UploadType.AUDIO;
      case FileAcceptType.VIDEO:
        return UploadType.VIDEO;
      case FileAcceptType.PDF:
        return UploadType.PDF;
      case FileAcceptType.FILE:
        return UploadType.FILE;
      default:
        return UploadType.IMAGE;
    }
  }, []);

  const uploadHandler = useCallback(async ({ file, type, imageUploadType }: UploadHandlerArgs) => {
    try {
      const { id, assemblyId } = (await new Promise((resolve) => {
        const req = new XMLHttpRequest();

        req.onreadystatechange = function () {
          if (this.readyState === 4 && this.status === 200) {
            resolve(JSON.parse(this.responseText));
          }
        };

        const formData = new FormData();

        formData.append('file', file as Blob);

        if (type === UploadType.IMAGE && imageUploadType) {
          formData.append('template', imageUploadType);
        }

        req.open(
          'POST',
          `${TCHOP_ROOT_URL}/api/fs/upload/${type}?organisation=${getOrganisationDomain()}`
        );

        prepareHeaders(new Headers()).forEach((value, key) => {
          req.setRequestHeader(key, value);
        });

        req.withCredentials = true;

        req.send(formData);
      })) as { id: number; assemblyId: string };

      return { id, assemblyId };
    } catch (_) {
      return { id: 0, assemblyId: '' };
    }
  }, []);

  return { uploadHandler, getUploadType, onUploadCompleted };
};
